import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const PermissionManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchAvailablePermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      // Get all users through admin endpoint
      const [studentsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/students`),
        axios.get(`${API_BASE_URL}/api/users`) // This should get all users
      ]);
      
      const students = studentsResponse.data || [];
      const allUsersFromUsersEndpoint = usersResponse.data || [];
      
      // Combine and deduplicate by user ID
      const userMap = new Map();
      
      // Add students first
      students.forEach(user => {
        userMap.set(user._id, user);
      });
      
      // Add other users, avoiding duplicates
      allUsersFromUsersEndpoint.forEach(user => {
        userMap.set(user._id, user);
      });
      
      // Convert back to array and sort by role and name
      const uniqueUsers = Array.from(userMap.values()).sort((a, b) => {
        // Sort by role first (super-admin, admin, grader, student), then by name
        const roleOrder = { 'super-admin': 0, admin: 1, grader: 2, student: 3 };
        const roleComparison = (roleOrder[a.role] || 4) - (roleOrder[b.role] || 4);
        if (roleComparison !== 0) return roleComparison;
        return a.name.localeCompare(b.name);
      });
      
      setUsers(uniqueUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/permissions`);
      setAvailablePermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      setActionLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/users/${userId}/permissions`);
      setUserPermissions(response.data);
      setSelectedUser(userId);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      setError('Failed to fetch user permissions');
    } finally {
      setActionLoading(false);
    }
  };

  const grantPermission = async (userId, permission) => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/permissions/allow`, {
        permission
      });
      setSuccess(`Permission '${permission}' granted successfully`);
      // Refresh user permissions
      await fetchUserPermissions(userId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error granting permission:', error);
      setError(error.response?.data?.message || 'Failed to grant permission');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const revokePermission = async (userId, permission) => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/permissions/deny`, {
        permission
      });
      setSuccess(`Permission '${permission}' revoked successfully`);
      // Refresh user permissions
      await fetchUserPermissions(userId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error revoking permission:', error);
      setError(error.response?.data?.message || 'Failed to revoke permission');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const clearAllOverrides = async (userId) => {
    try {
      setActionLoading(true);
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}/permissions`);
      setSuccess('All permission overrides cleared successfully');
      // Refresh user permissions
      await fetchUserPermissions(userId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error clearing overrides:', error);
      setError(error.response?.data?.message || 'Failed to clear overrides');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const getPermissionDescription = (permission) => {
    const descriptions = {
      'submit': 'Submit solutions to problems',
      'submissions:view:own': 'View own submissions',
      'submissions:view:all': 'View all submissions',
      'grade:submissions': 'Grade student submissions',
      'grade:overwrite:any': 'Overwrite any existing grade',
      'tests:view': 'View tests and problems',
      'tests:create': 'Create new tests',
      'tests:update': 'Update existing tests',
      'tests:delete': 'Delete tests',
      'users:view': 'View user information',
      'users:manage': 'Manage user accounts and permissions',
      'users:approve:graders': 'Approve grader applications',
      'stats:view': 'View system statistics',
      'system:manage': 'System administration',
      'view:own': 'View own resources',
      'view:any': 'View any resources'
    };
    return descriptions[permission] || permission;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>👑 Permission Management</h1>
      
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {success}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
        {/* Users List */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0 }}>👥 Users</h2>
            <span style={{ fontSize: '14px', color: '#666' }}>
              {users.filter(user => 
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.role.toLowerCase().includes(searchTerm.toLowerCase())
              ).length} of {users.length}
            </span>
          </div>
          
          {/* Search Bar */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="🔍 Search users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100%',
                backgroundColor: '#f8f9fa'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#666',
                  marginTop: '-16px',
                  marginRight: '20px'
                }}
              >
                ✕
              </button>
            )}
          </div>
          
          {users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              No users found
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {users
                .filter(user => 
                  user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  user.role.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((user) => (
                <div
                  key={user._id}
                  onClick={() => fetchUserPermissions(user._id)}
                  className={`user-item ${selectedUser === user._id ? 'selected' : ''}`}
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: selectedUser === user._id ? '#e3f2fd' : 'white',
                    borderColor: selectedUser === user._id ? '#2196f3' : '#ddd',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ fontWeight: '500' }}>{user.name}</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {user.email}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                    <span 
                      className="badge"
                      style={{
                        backgroundColor: user.role === 'super-admin' ? '#9c27b0' :
                                       user.role === 'admin' ? '#f44336' : 
                                       user.role === 'grader' ? '#ff9800' : '#4caf50',
                        color: 'white'
                      }}
                    >
                      {user.role === 'super-admin' ? '🛡️ SUPER-ADMIN' : user.role}
                    </span>
                    <span className={`badge ${user.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                      {user.status || 'active'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Permission Details */}
        <div className="card">
          {!selectedUser ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <h3>Select a user to manage permissions</h3>
              <p>Click on a user from the list to view and modify their permissions</p>
            </div>
          ) : actionLoading ? (
            <div className="loading">
              <div className="spinner"></div>
              Loading permissions...
            </div>
          ) : userPermissions ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>🔐 Permissions for {userPermissions.user.name}</h2>
                <button
                  onClick={() => clearAllOverrides(selectedUser)}
                  className="btn btn-warning"
                  disabled={actionLoading}
                  style={{ fontSize: '14px' }}
                >
                  Clear All Overrides
                </button>
              </div>

              {/* Role Information */}
              <div className="card" style={{ backgroundColor: '#f8f9fa', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0' }}>
                  📋 Role: {userPermissions.user.role}
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                    ({userPermissions.rolePermissions.length} base permissions)
                  </span>
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <strong>Role Permissions:</strong>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '6px', 
                    marginTop: '8px' 
                  }}>
                    {userPermissions.rolePermissions.map(permission => (
                      <span 
                        key={permission} 
                        className="badge badge-secondary"
                        style={{ fontSize: '12px' }}
                        title={getPermissionDescription(permission)}
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Overrides */}
                {(userPermissions.permOverrides.allow.length > 0 || userPermissions.permOverrides.deny.length > 0) && (
                  <div>
                    {userPermissions.permOverrides.allow.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong style={{ color: '#4caf50' }}>✅ Granted Overrides:</strong>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '6px', 
                          marginTop: '8px' 
                        }}>
                          {userPermissions.permOverrides.allow.map(permission => (
                            <span 
                              key={permission} 
                              className="badge"
                              style={{ 
                                backgroundColor: '#4caf50', 
                                color: 'white',
                                fontSize: '12px' 
                              }}
                              title={getPermissionDescription(permission)}
                            >
                              +{permission}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {userPermissions.permOverrides.deny.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong style={{ color: '#f44336' }}>❌ Revoked Overrides:</strong>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '6px', 
                          marginTop: '8px' 
                        }}>
                          {userPermissions.permOverrides.deny.map(permission => (
                            <span 
                              key={permission} 
                              className="badge"
                              style={{ 
                                backgroundColor: '#f44336', 
                                color: 'white',
                                fontSize: '12px' 
                              }}
                              title={getPermissionDescription(permission)}
                            >
                              -{permission}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Effective Permissions */}
              <div className="card" style={{ backgroundColor: '#e8f5e8', marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#2e7d32' }}>
                  🎯 Effective Permissions 
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                    ({userPermissions.effectivePermissions.length} total)
                  </span>
                </h3>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px' 
                }}>
                  {userPermissions.effectivePermissions.map(permission => (
                    <span 
                      key={permission} 
                      className="badge"
                      style={{ 
                        backgroundColor: '#2e7d32', 
                        color: 'white',
                        fontSize: '12px' 
                      }}
                      title={getPermissionDescription(permission)}
                    >
                      {permission}
                    </span>
                  ))}
                </div>
              </div>

              {/* Permission Actions */}
              <div className="card">
                <h3 style={{ margin: '0 0 16px 0' }}>⚙️ Modify Permissions</h3>
                
                {/* Super-admin protection notice */}
                {userPermissions.user.role === 'super-admin' && (
                  <div style={{ 
                    marginBottom: '16px', 
                    padding: '12px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '6px',
                    color: '#856404'
                  }}>
                    <strong>🛡️ Super-Admin Protection:</strong> Super-admins have all permissions by default and cannot be modified (except by other super-admins).
                  </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Grant Permission */}
                  <div>
                    <h4 style={{ color: '#4caf50', margin: '0 0 12px 0' }}>✅ Grant Permission</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {availablePermissions
                        .filter(perm => !userPermissions.effectivePermissions.includes(perm.value))
                        .map(permission => (
                          <button
                            key={permission.value}
                            onClick={() => grantPermission(selectedUser, permission.value)}
                            className="btn btn-outline"
                            style={{ 
                              textAlign: 'left', 
                              padding: '8px 12px',
                              fontSize: '13px',
                              borderColor: '#4caf50',
                              color: '#4caf50'
                            }}
                            disabled={actionLoading}
                            title={permission.description || getPermissionDescription(permission.value)}
                          >
                            + {permission.value}
                          </button>
                        ))}
                      {availablePermissions.filter(perm => !userPermissions.effectivePermissions.includes(perm.value)).length === 0 && (
                        <div style={{ color: '#666', fontStyle: 'italic' }}>
                          User has all available permissions
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Revoke Permission */}
                  <div>
                    <h4 style={{ color: '#f44336', margin: '0 0 12px 0' }}>❌ Revoke Permission</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {userPermissions.effectivePermissions.map(permission => (
                        <button
                          key={permission}
                          onClick={() => revokePermission(selectedUser, permission)}
                          className="btn btn-outline"
                          style={{ 
                            textAlign: 'left', 
                            padding: '8px 12px',
                            fontSize: '13px',
                            borderColor: '#f44336',
                            color: '#f44336'
                          }}
                          disabled={actionLoading}
                          title={getPermissionDescription(permission)}
                        >
                          - {permission}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PermissionManagement;
